import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ImageOptimizer } from '../utils/imageOptimization';

/**
 * Hook to preload critical images based on current route
 * Updated: 2024-12-27 - Fixed preload warnings by only loading images where they're actually used
 */
export const useImagePreloader = () => {
  const location = useLocation();

  useEffect(() => {
    const preloadImagesForRoute = () => {
      const criticalImages: string[] = [];

      // Add route-specific critical images only where they're actually used
      switch (location.pathname) {
        case '/':
          // Homepage - no critical images needed immediately
          break;
        
        case '/dashboard':
          // Dashboard critical images - empty state shown when no projects
          criticalImages.push(
            '/empty-state.svg',
            // Add optimized versions of commonly shown demo avatars
            ImageOptimizer.optimizeImage(
              'https://images.pexels.com/photos/1462636/pexels-photo-1462636.jpeg',
              { width: 32, height: 32, format: 'webp' }
            )
          );
          break;
        
        case '/gallery':
          // Gallery - empty state shown when no projects in gallery
          criticalImages.push('/empty-state.svg');
          break;
        
        case '/editor':
          // Editor - no immediate image preloading needed
          break;
        
        default:
          // Don't preload empty-state.svg on routes where it's not immediately used
          break;
      }

      // Preload the images
      if (criticalImages.length > 0) {
        ImageOptimizer.preloadImages(criticalImages);
      }
    };

    // Small delay to avoid blocking initial render
    const timeoutId = setTimeout(preloadImagesForRoute, 100);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname]);
};

/**
 * Hook to preload demo user avatars when demo mode is enabled
 */
export const useDemoAvatarPreloader = (isDemoMode: boolean) => {
  useEffect(() => {
    if (!isDemoMode) return;

    // Common demo user avatars that are likely to be shown
    const demoAvatars = [
      'https://images.pexels.com/photos/1462636/pexels-photo-1462636.jpeg',
      'https://images.pexels.com/photos/1379636/pexels-photo-1379636.jpeg',
      'https://images.pexels.com/photos/1157557/pexels-photo-1157557.jpeg',
      'https://images.pexels.com/photos/1516680/pexels-photo-1516680.jpeg',
      'https://images.pexels.com/photos/1462637/pexels-photo-1462637.jpeg'
    ];

    // Preload optimized versions
    const optimizedAvatars = demoAvatars.map(url => 
      ImageOptimizer.optimizeImage(url, { 
        width: 32, 
        height: 32, 
        format: 'webp',
        quality: 85 
      })
    );

    // Delay preloading to not interfere with critical resources
    const timeoutId = setTimeout(() => {
      ImageOptimizer.preloadImages(optimizedAvatars);
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [isDemoMode]);
};